import { convertToText } from './rich-text.helpers';

/**
 * Client-side filtering for educations to avoid server action payload issues
 */

export const filterEducations = (educations, filters) => {
  if (!educations || !filters || filters.length === 0) {
    return educations;
  }

  let filteredEducations = [...educations];

  // Search filter
  const searchFilters = filters.filter((filter) => filter.type === 'search');
  if (searchFilters.length > 0) {
    filteredEducations = filteredEducations.filter((education) => {
      const title = education.title || '';
      const description = education.description || '';
      const contentText = education.content ? convertToText(education.content) : '';
      const lecturers =
        education.modules?.modules
          ?.flatMap((module) =>
            (module.lecturers || []).map((entry) => entry?.lecturer?.fullName || '').filter(Boolean)
          )
          .join(', ') || '';

      return searchFilters.every((filter) => {
        const searchValue = filter.value.toLowerCase();
        return (
          title.toLowerCase().includes(searchValue) ||
          description.toLowerCase().includes(searchValue) ||
          contentText.toLowerCase().includes(searchValue) ||
          lecturers.toLowerCase().includes(searchValue)
        );
      });
    });
  }

  // Duration filters
  const durationLongFilter = filters.find((filter) => filter.type === 'durationLong');
  if (durationLongFilter) {
    filteredEducations = filteredEducations.filter((education) => {
      const modules = education.modules?.modules || [];
      const totalDuration = modules.reduce((sum, module) => sum + (module.lectureDuration || 0), 0);
      return totalDuration >= 180;
    });
  }

  const durationShortFilter = filters.find((filter) => filter.type === 'durationShort');
  if (durationShortFilter) {
    filteredEducations = filteredEducations.filter((education) => {
      const modules = education.modules?.modules || [];
      const totalDuration = modules.reduce((sum, module) => sum + (module.lectureDuration || 0), 0);
      return totalDuration < 180;
    });
  }

  // Type filters
  const typeFilters = filters.filter((filter) =>
    ['online', 'live', 'recording'].includes(filter.type)
  );
  if (typeFilters.length > 0) {
    filteredEducations = filteredEducations.filter((education) => {
      const venues = education.venues || [];
      return typeFilters.every((filter) => venues.includes(filter.type));
    });
  }

  // Upcoming filter
  const upcomingFilter = filters.find((filter) => filter.type === 'upcoming');
  if (upcomingFilter) {
    filteredEducations = filteredEducations.filter((education) => {
      const modules = education.modules?.modules || [];
      return modules.some(
        (module) =>
          module.lectureStartDateTime && new Date(module.lectureStartDateTime) > new Date()
      );
    });

    // Sort by start date if we're filtering for upcoming
    filteredEducations = filteredEducations.sort((a, b) => {
      const getNextDate = (education) => {
        const upcomingDates = education.modules.modules
          .filter((m) => new Date(m.lectureStartDateTime) > new Date())
          .map((m) => new Date(m.lectureStartDateTime));
        return upcomingDates.length > 0 ? Math.min(...upcomingDates) : Infinity;
      };

      return getNextDate(a) - getNextDate(b);
    });
  }

  return filteredEducations;
};

/**
 * Check if an education has any active discount
 */
export const hasActiveDiscount = (education) => {
  if (!education.plans || !education.plans.educationPlans) return false;

  const now = new Date();

  return education.plans.educationPlans
    .filter((planItem) => {
      const hasActiveFrom = planItem.activeFrom && planItem.activeFrom !== '';
      const hasActiveTo = planItem.activeTo && planItem.activeTo !== '';

      // If neither is set, include the item
      if (!hasActiveFrom && !hasActiveTo) return true;

      // Check only the constraints that are set
      const fromCheck = !hasActiveFrom || new Date(planItem.activeFrom) < now;
      const toCheck = !hasActiveTo || new Date(planItem.activeTo) > now;

      return fromCheck && toCheck;
    })
    .some((planItem) => {
      const discount = planItem.discount || 0;
      const discountExpiresAt = planItem.discountExpiresAt;

      // Check if discount exists and is active
      if (discount > 0) {
        // If no expiration date, discount is active
        if (!discountExpiresAt) return true;

        // Check if discount hasn't expired
        return new Date(discountExpiresAt) > now;
      }

      return false;
    });
};

export const filterEducationsByTag = (educations, tag) => {
  if (!tag || tag === 'featured') {
    return educations;
  }

  // Handle discount tag
  if (tag === 'discount') {
    return educations.filter(hasActiveDiscount);
  }

  return educations.filter((education) => education.tags?.map((tag) => tag.slug).includes(tag));
};
